import { useRef, useState, useCallback } from 'react'

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    {
      urls: 'turn:openrelay.metered.ca:80',
      username: 'openrelayproject',
      credential: 'openrelayproject'
    },
    {
      urls: 'turn:openrelay.metered.ca:443',
      username: 'openrelayproject',
      credential: 'openrelayproject'
    }
  ]
}

export default function useWebRTC({ onRemoteStream, onDataChannel, onConnectionEnded, onConnectionConnected }) {
  const pcRef = useRef(null)
  const [connectionState, setConnectionState] = useState('new')

  const createPeer = useCallback((onIceCandidate) => {
    // Close any existing peer connection to prevent leaks and stale state
    if (pcRef.current) {
      console.log('[WebRTC] Closing existing peer connection before creating new one')
      pcRef.current.close()
      pcRef.current = null
    }

    const pc = new RTCPeerConnection(ICE_SERVERS)
    pcRef.current = pc

    pc.onconnectionstatechange = () => {
      console.log('CONNECTION STATE:', pc.connectionState)
      setConnectionState(pc.connectionState)
      if (pc.connectionState === 'connected') {
        onConnectionConnected?.()
      }
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        if (pcRef.current === pc) {
          pcRef.current = null
        }
        onConnectionEnded?.()
      }
    }

    pc.onsignalingstatechange = () => {
      console.log('SIGNALING STATE:', pc.signalingState)
    }

    pc.onicegatheringstatechange = () => {
      console.log('ICE GATHERING:', pc.iceGatheringState)
    }

    pc.oniceconnectionstatechange = () => {
      console.log('ICE CONNECTION:', pc.iceConnectionState)
      if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'closed' || pc.iceConnectionState === 'disconnected') {
        pc.close()
        if (pcRef.current === pc) {
          pcRef.current = null
        }
        setConnectionState('closed')
        onConnectionEnded?.()
      }
    }

    pc.onicecandidate = (event) => {
      console.log('ICE CANDIDATE:', event.candidate)
      if (event.candidate) {
        onIceCandidate?.(event.candidate)
      }
    }

    pc.ontrack = (event) => {
      if (event.streams && event.streams[0]) {
        onRemoteStream?.(event.streams[0])
      }
    }

    pc.ondatachannel = (event) => {
      const channel = event.channel
      channel.onmessage = (e) => {
        onDataChannel?.(e.data)
      }
    }

    return pc
  }, [onRemoteStream, onDataChannel, onConnectionEnded, onConnectionConnected])

  const addLocalStream = useCallback((stream) => {
    if (!pcRef.current) return
    stream.getTracks().forEach((track) => {
      pcRef.current.addTrack(track, stream)
    })
  }, [])

  const createOffer = useCallback(async () => {
    if (!pcRef.current) return null
    const offer = await pcRef.current.createOffer()
    await pcRef.current.setLocalDescription(offer)
    return offer
  }, [])

  const handleAnswer = useCallback(async (answer) => {
    if (!pcRef.current) return
    await pcRef.current.setRemoteDescription(new RTCSessionDescription(answer))
  }, [])

  const handleOffer = useCallback(async (offer, onAnswer, onIceCandidate) => {
    if (!pcRef.current) return
    await pcRef.current.setRemoteDescription(new RTCSessionDescription(offer))
    const answer = await pcRef.current.createAnswer()
    await pcRef.current.setLocalDescription(answer)
    onAnswer?.(answer)
    // Chain ICE candidate handler so logging and external callbacks both fire
    const prevHandler = pcRef.current.onicecandidate
    pcRef.current.onicecandidate = (event) => {
      prevHandler?.(event)
      if (event.candidate) {
        onIceCandidate?.(event.candidate)
      }
    }
  }, [])

  const handleIceCandidate = useCallback(async (candidate) => {
    if (!pcRef.current) return
    await pcRef.current.addIceCandidate(new RTCIceCandidate(typeof candidate === "string" ? { candidate, sdpMid: "0", sdpMLineIndex: 0 } : candidate))
  }, [])

  const createDataChannel = useCallback((label) => {
    if (!pcRef.current) return null
    return pcRef.current.createDataChannel(label)
  }, [])

  const close = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.close()
      pcRef.current = null
    }
    setConnectionState('closed')
  }, [])

  return {
    connectionState,
    pcRef,
    createPeer,
    addLocalStream,
    createOffer,
    handleAnswer,
    handleOffer,
    handleIceCandidate,
    createDataChannel,
    close,
  }
}





